<template>
  <div
      class="id-gen"
      :class="{ 'id-gen--editing': editing, 'id-gen--invalid': editing && !valid }"
  >
    <div class="id-gen__info">
      <div class="id-gen__label">{{ editing ? 'CUSTOM TRACKER ID' : title }}</div>

      <input
          v-if="editing"
          ref="inputRef"
          v-model="draft"
          type="text"
          inputmode="numeric"
          class="id-gen__input"
          :maxlength="length"
          :placeholder="`${min}–${length} digits`"
          @keydown.enter="apply"
          @keydown.esc="cancel"
      >
      <div
          v-else
          class="id-gen__value"
      >{{ display }}</div>
      <input type="hidden" :name="name" :value="display">
    </div>

    <!-- Edit-mode actions -->
    <div
        v-if="editing"
        class="id-gen__actions"
    >
      <button
          type="button"
          @click="cancel"
      >Cancel</button>
      <button
          type="button"
          class="id-gen__btn--apply"
          :disabled="!valid"
          @click="apply"
      >
        <i class="icon-check" />Apply
      </button>
    </div>

    <!-- View-mode actions -->
    <div
        v-else
        class="id-gen__actions"
    >
      <button
          type="button"
          @click="copy"
      >
        <i :class="copied ? 'icon-check' : 'icon-copy'" />{{ copied ? 'Copied' : 'Copy' }}
      </button>
      <template v-if="!copyOnly">
        <button
            type="button"
            @click="regenerate"
        >
          <i class="icon-refresh" />Regenerate
        </button>
        <button
            type="button"
            @click="useMyOwn"
        >
          <i class="icon-edit" />Use my own
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useEmitter } from "@/js/Plugins/Emitter.js";

const emitter = useEmitter();

const props = defineProps({
  title: {
    type: String,
    default: 'ID'
  },
  length: {
    type: Number,
    default: 6
  },
  min: {
    type: Number,
    default: 1
  },
  modelValue: {
    type: String,
    default: ''
  },
  name: String,
  // Display + copy only — hides Regenerate / Use my own.
  copyOnly: Boolean,
});

const emit = defineEmits(['update:modelValue']);

const COPY_FEEDBACK_MS = 1500;

function randomId(len) {
  let out = '';
  for (let i = 0; i < len; i++) out += Math.floor(Math.random() * 10);
  return out;
}

const value = ref(props.modelValue || randomId(props.length));
const draft = ref('');
const editing = ref(false);
const copied = ref(false);
const inputRef = ref();
let copyTimer = null;

const display = computed(() => value.value);

const valid = computed(() => new RegExp(`^[0-9]{${props.min},${props.length}}$`).test(draft.value));

watch(draft, (v) => {
  const clean = String(v ?? '').replace(/[^0-9]/g, '').slice(0, props.length);
  if (clean !== v) draft.value = clean;
});

watch(value, (v) => emit('update:modelValue', v), { immediate: true });
watch(editing, (v) => emitter?.emit('id-generator:editing', { name: props.name, editing: v }), { immediate: true });
onBeforeUnmount(() => emitter?.emit('id-generator:editing', { name: props.name, editing: false }));

function copy() {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value.value);
  }
  copied.value = true;
  clearTimeout(copyTimer);
  copyTimer = setTimeout(() => (copied.value = false), COPY_FEEDBACK_MS);
}

function regenerate() {
  editing.value = false;
  value.value = randomId(props.length);
}

function useMyOwn() {
  draft.value = value.value;
  editing.value = true;
  nextTick(() => inputRef.value?.focus());
}

function cancel() {
  editing.value = false;
  draft.value = value.value;
}

function apply() {
  if (!valid.value) return;
  value.value = draft.value;
  editing.value = false;
}
</script>

<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.id-gen {
  width: 100%;
  display: flex;
  align-items: center;
  gap: toRem(12);
  padding: toRem(14) toRem(16);
  background: themeColor('primary-50');
  border: toRem(1) dashed themeColor('primary-500');
  border-radius: $border-radius-lg;

  // Edit mode — solid white block with a brand focus ring.
  &--editing {
    background: themeColor('base');
    border-style: solid;
    border-color: themeColor('primary-500');
    box-shadow: 0 0 0 toRem(3) rgba(28, 180, 217, .14);
  }

  // Invalid draft — red border + ring.
  &--invalid {
    border-color: themeColor('negative-600');
    box-shadow: 0 0 0 toRem(3) rgba(200, 49, 49, .10);
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: toRem(4);
  }

  &__label {
    font-size: fontSize(5);
    line-height: toRem(16);
    letter-spacing: .04em;
    text-transform: uppercase;
    color: themeColor('primary-700');
  }

  &__value {
    font-size: toRem(22);
    line-height: 1;
    font-weight: $font-weight-bold;
    letter-spacing: toRem(2);
    color: themeColor('primary-700');
    font-feature-settings: "tnum";
  }

  // Borderless big-text input that sits flush inside the block.
  &__input {
    width: 100%;
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    outline: 0;
    font-size: toRem(22);
    line-height: 1;
    font-weight: $font-weight-bold;
    letter-spacing: toRem(2);
    color: themeColor('primary-700');
    font-feature-settings: "tnum";

    &::placeholder {
      font-size: toRem(15);
      letter-spacing: 0;
      color: themeColor('neutral-500');
    }
  }

  &--invalid &__input { color: themeColor('negative-600'); }

  &__actions {
    flex-shrink: 0;
    display: flex;
    gap: toRem(6);

    button {
      display: flex;
      align-items: center;
      gap: toRem(6);
      height: toRem(32);
      padding: 0 toRem(10);
      background: themeColor('base');
      border: toRem(1) solid themeColor('primary-500');
      border-radius: toRem(6);
      font-size: toRem(12);
      line-height: 1;
      font-weight: $font-weight-semibold;
      color: themeColor('primary-700');
      cursor: pointer;
      white-space: nowrap;

      &:hover { background: themeColor('primary-100'); }
    }

    // Filled "Apply" action.
    .id-gen__btn--apply {
      background: themeColor('primary-500');
      border-color: themeColor('primary-500');
      color: themeColor('base');

      &:hover {
        background: themeColor('primary-600');
        border-color: themeColor('primary-600');
      }

      &:disabled {
        background: themeColor('neutral-300');
        border-color: themeColor('neutral-300');
        color: themeColor('base');
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }
}
</style>
